// 141 §2.2 — THE PER-MATTER CLIENT STATUS, AS A PILL. ONE OWNER, TWO SURFACES.
//
// §0-bis ruled that a client's status belongs to the LINK between a client and
// a matter, and that the vocabulary is the same for both kinds of matter. §1
// built the control on the collective roster: the five values, their colours,
// and the shared `StatusPill` menu (136 §1) rather than a second pill. §2.2
// asks for "the same affordance the CCV roster has, on the case's client list.
// Same vocabulary, same colours".
//
// SAME means one definition. A copy of the colour table in `CaseView.tsx` is
// exactly the defect 115 closed for the Dashboard's rows and 117 §2a closed for
// the subtask pill — two renderers for one thing, every later fix landing in
// one of them. So the colours and the option list move here, where the CCV's
// closure had them, and both views mount the same rows.
//
// WHAT IS *NOT* HERE: the vocabulary itself (`KNOWN_STATUSES`, imported) and
// the date slot each value stamps (`LINK_STATUS_DATE`, same module). Those are
// facts about the value and are read by the server too; this file is
// presentation and imports React types.
import type { StatusPillCfg, StatusPillOption } from "../components/StatusPill";
import { KNOWN_STATUSES } from "./collectiveSegments.mjs";

/** `useTranslation()`'s resolver, narrowed to what this file passes. */
export type TFn = (
  key: string,
  vars?: { fallback?: string } & Record<string, unknown>,
) => string;

/** The ink and the ground a value wears. Both are TOKENS: §4.9 forbids a
 *  hardcoded colour in new UI, and every ground derives from its own ink
 *  through App.css's own `color-mix` recipe rather than being a second hex
 *  somebody has to keep in step with the first. */
export const LINK_STATUS_COLORS: Record<string, { bg: string; c: string }> = {
  // Prospect wears `--pp`, the token 140 §1.2 gave the prospect pill, and
  // Archived wears `--c-b87333`, the one `.status-archived` already uses.
  // Reuse, do not invent (141 §1.3).
  Prospect: {
    bg: "color-mix(in srgb, var(--pp) 16%, transparent)",
    c: "var(--pp)",
  },
  Active: { bg: "var(--gs)", c: "var(--gn)" },
  Suspended: { bg: "var(--ams)", c: "var(--am)" },
  Terminated: { bg: "var(--rs)", c: "var(--rd)" },
  Archived: {
    bg: "color-mix(in srgb, var(--c-b87333) 16%, transparent)",
    c: "var(--c-b87333)",
  },
};

/* NO STATUS IS NOT A SIXTH STATUS.
   Every collective member has a value — `addClient` enters one as Active — but
   a regular case's link is NULL until somebody rules on it, and that is every
   link that exists today (§2.1's migration adds no default and backfills
   nothing). The pill has to draw something, and the honest something is "this
   link has not been given one", in the muted pair the app uses for absence,
   with the menu beside it offering the five it could take.
   It is deliberately NOT the case's own status. The client record falls back
   to that (§2.3, ruled), because there the question is "what is this client's
   standing in this matter" and the matter's status is the best available
   answer. Here the question is "what has this client been given", and painting
   the case's `pending` into a pill whose menu offers Prospect/Active/Suspended
   would put two vocabularies in one control.
   Its colours are written INTO `linkStatusCfg` below rather than kept as a
   named pair up here: a named map of two strings is a prose map to
   `scripts/lint/i18n-total.js`'s detector, and declaring an exemption would be
   asserting that a literal reaches a human, which these do not. */

/** The label for one value, through the SAME keys the segment panel resolves.
 *  Falls back to the raw value, so a legacy status outside the vocabulary
 *  still shows itself rather than vanishing. */
export function linkStatusLabel(t: TFn, s: string | null | undefined): string {
  if (!s) return "";
  return t("cc.retainer.status." + String(s).toLowerCase(), { fallback: s });
}

/** The pill cfg for a link's current value — `null` included. */
export function linkStatusCfg(
  t: TFn,
  s: string | null | undefined,
): StatusPillCfg {
  if (!s) {
    // The muted pair the app uses for absence everywhere — ink `--tm`, ground
    // `--bi`. Tokens, never hex (§4.9).
    return {
      label: t("link.status.unset", { fallback: "No status" }),
      color: "var(--tm)",
      bgColor: "var(--bi)",
    };
  }
  const c = LINK_STATUS_COLORS[s] || LINK_STATUS_COLORS.Active;
  return { label: linkStatusLabel(t, s), color: c.c, bgColor: c.bg };
}

/** The picker's rows: the vocabulary in lifecycle order (KNOWN_STATUSES is
 *  ordered Prospect → … → Archived, so consumers that map it render the ladder
 *  for free), each wearing its own colours — StatusPill draws its swatch from
 *  `cfg.color`. */
export function linkStatusOptions(t: TFn): StatusPillOption[] {
  return (KNOWN_STATUSES as string[]).map((s) => ({
    code: s,
    cfg: linkStatusCfg(t, s),
  }));
}
